import { LocalStorageUtils } from '@assetmark/ui-kit/dist/utils/local-storage.utils';
import { RemoteData, RemoteDataUtils } from '@assetmark/ui-kit/dist/utils/remote-data.utils';
import { fromNullable } from 'fp-ts/lib/Option';
import { BehaviorSubject, merge, of, Subject } from 'rxjs';
import {
	GOAL_KEY,
	LEAD_APP_STATE,
	LEAD_ID_KEY,
	LEAD_STRATEGY_ID_KEY,
	GOAL_ID_KEY,
	ADVISOR_ID_KEY,
} from '../constants/storage.constant';
import { getRedirectPage } from '../helpers/pagehelper';
import { ApplicationStateService } from '../lib/das-api/api/applicationState.service';
import { LeadService } from '../lib/das-api/api/lead.service';
import { TAddress, TApplicationState } from '../models/application-state';
import { TLeadStrategyInfoResponse } from '../models/lead-strategy-info-response';
import { TLeadGoalsResponse } from '../models/goals-info-response';
import { LeadApi } from './lead.api';
import { GoalsApi } from './goals.api';
import { IApplicationState } from '../lib/das-api/model/iApplicationState';
import { Address } from '../lib/das-api/model/address';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace AppApi {
	export const advisorId$ = new BehaviorSubject<string>('');
	export const isAuthCallComplete$ = new BehaviorSubject<boolean>(false);
	export const spinner$ = () => LeadApi.showSpinner$.asObservable();

	export const leadStrategiesData$ = new BehaviorSubject<RemoteData<TLeadStrategyInfoResponse>>(
		RemoteDataUtils.initial,
	);

	export const getSavedLeadStrategiesData$ = () => leadStrategiesData$.asObservable();

	// Get saved Goals Data
	export const leadGoalsData$ = new BehaviorSubject<RemoteData<TLeadGoalsResponse>>(RemoteDataUtils.initial);
	export const getGoalsData$ = () => leadGoalsData$.asObservable();

	export function getNextPageToRedirect$() {
		return LeadApi.redirectToPage$.asObservable();
	}

	export const getAppStateData = (advisorId: string) => {
		if (advisorId) {
			return ApplicationStateService.apiDasV1ApplicationStateGet(advisorId).mapRD(parseAppState).shareReplay(1);
		} else {
			return of(RemoteDataUtils.initial);
		}
	};

	export const appStateData$ = advisorId$.switchMap(advisorId => getAppStateData(advisorId));

	export const getSavedLeadProfileData = (leadId: string, advisorId: string) => {
		if (advisorId && leadId) {
			return LeadService.apiDasV1LeadLeadIdGet(leadId, advisorId).mapRD(LeadApi.parseLead).shareReplay(1);
		} else {
			return of(RemoteDataUtils.initial);
		}
	};

	export const getSavedLeadStrategiesData = (leadId: string, leadStrategyId: string, advisorId: string) => {
		if (advisorId && leadId && leadStrategyId) {
			return LeadService.apiDasV1LeadLeadIdLeadstrategiesLeadStrategyIdGet(leadId, leadStrategyId, advisorId)
				.mapRD(LeadApi.parseLeadStrategy)
				.doRD(data => {
					if(data.lead.isNone()) {
						leadStrategiesData$.next(RemoteDataUtils.failure(new Error("Lead deleted or converted to prospect"))); 
					} else { 
						leadStrategiesData$.next(RemoteDataUtils.success(data)); 
					}
				})
				.shareReplay(1);
		} else {
			return of(RemoteDataUtils.initial);
		}
	};

	export const getLeadProfile$ = advisorId$.switchMap(advisorId => {
		const leadId = LocalStorageUtils.getItem<string>(LEAD_ID_KEY);
		return getSavedLeadProfileData(leadId.getOrElse(''), advisorId);
	});

	// this function being called when application loads and check saved data.
	export const getLeadStrategies$ = advisorId$.switchMap(advisorId => {
		const savedAdvisortId = LocalStorageUtils.getItem<string>(ADVISOR_ID_KEY);
		const leadId = LocalStorageUtils.getItem<string>(LEAD_ID_KEY);
		const leadStrategyId = LocalStorageUtils.getItem<string>(LEAD_STRATEGY_ID_KEY);
		if (savedAdvisortId.getOrElse('').toLowerCase() === advisorId.toLowerCase()) {
			return getSavedLeadStrategiesData(leadId.getOrElse(''), leadStrategyId.getOrElse(''), advisorId);
		} else {
			return of(RemoteDataUtils.initial);
		}
	});

	// saved goals data
	export const getGoalsData = (goalId: string, agId: string) => {
		if (goalId) {
			const response$ = LeadService.apiDasV1LeadGoalsGoalIdGet(goalId, agId)
				.mapRD(GoalsApi.parseGoals)
				.shareReplay(1);
			response$.subscribe(data => {
				if (data.isSuccess()) {
					leadGoalsData$.next(data);
				}
			});
		} else {
			return of(RemoteDataUtils.initial);
		}
	};

	export const getLeadGoals$ = advisorId$.switchMap(advisorId => {
		const savedGoalId = LocalStorageUtils.getItem<string>(GOAL_ID_KEY);
		const savedAdvisortId = LocalStorageUtils.getItem<string>(ADVISOR_ID_KEY);

		if (
			savedAdvisortId.getOrElse('').toLowerCase() === advisorId.toLowerCase() &&
			savedGoalId.getOrElse('') !== ''
		) {
			return LeadService.apiDasV1LeadGoalsGoalIdGet(savedGoalId.getOrElse(''), advisorId)
				.mapRD(GoalsApi.parseGoals)
				.do(data => data.isSuccess() && leadGoalsData$.next(data))
				.shareReplay(1);
		} else {
			return of(RemoteDataUtils.initial);
		}
	});

	// export const getSavedGoalsData = () => {
	//     const goalID = LocalStorageUtils.getItem<string>(GOAL_ID_KEY);
	//     const agId = LocalStorageUtils.getItem<string>(ADVISOR_ID_KEY);
	//     return getGoalsData(goalID.getOrElse(''), agId.getOrElse(''));
	// };

	export const sync$ = merge(advisorId$, getLeadStrategies$, getLeadGoals$);
}

const parseAppState = (app: IApplicationState): TApplicationState => {
	return {
		investorStrategyId: fromNullable(app.investorStrategyId),
		investorId: fromNullable(app.investorId),
		goalId: fromNullable(app.goalId),
		lastPageVisited: fromNullable(app.lastPageVisited),
		feeId: fromNullable(app.feeId),
		wordMark: fromNullable(app.wordMark),
		advisorLogo: fromNullable(app.advisorLogo),
		isLogoPreferred: fromNullable(app.isLogoPreferred),
		agentId: fromNullable(app.agentId),
		agentName: fromNullable(app.agentName),
		companyName: fromNullable(app.companyName),
		isDiscretionary: fromNullable(app.isDiscretionary),
		customProposalIntroductionText: fromNullable(app.customProposalIntroductionText),
		channelRequireGfamSuitability: fromNullable(app.channelRequireGfamSuitability),
		defaultIntroductionText: fromNullable(app.defaultIntroductionText),
		address: fromNullable(parseAddress(app.address || {})),
		phoneNumber: fromNullable(app.phoneNumber),
		advisorPhoto: fromNullable(app.advisorPhoto ? `/api/das/v1/Resources?url=${app.advisorPhoto}` : null),
	};
};

const parseAddress = (address: Address): TAddress => {
	return {
		city: fromNullable(address.city),
		line1: fromNullable(address.line1),
		line2: fromNullable(address.line2),
		line3: fromNullable(address.line3),
		postalCode: fromNullable(address.postalCode),
		id: fromNullable(address.id),
	};
};
